import type {
  KomojuPaymentStatus,
  PurchaseInfo,
  PurchaseProviderCreditCardBrand,
} from '@play-co/replicant';
import {
  LOCAL_CURRENCY,
  PENDING_PAYMENT_CLEANUP,
  PENDING_PAYMENT_TIMEOUT,
  webIAPPrices,
  type ProductId,
} from './iap.ruleset';
import type {
  KomojuPaymentMethod,
  WebPurchaseProvider,
} from 'src/game/iap/iap.types';
import type { MutableState, State } from '../State';
import type { Immutable } from '../utils/types';
import { assertNever } from '../utils';
import { createReportableError } from '../utils/errors';
import type { RedirectStateAnalytics } from 'src/game/iap/analytics/analytics.types';
import { Feature } from '../analytics';

/**
 * List of supported pending payment providers. Need to be a list so we can check it in runtime.
 */
const supportedPendingPaymentProviders = [
  'komoju',
] satisfies WebPurchaseProvider[];

/**
 * Supported payment platforms that have a pending payment.
 * In our case it's only Komoju.
 */
type PendingPaymentProvider = Extract<
  WebPurchaseProvider,
  (typeof supportedPendingPaymentProviders)[number]
>;

/**
 * Our internal representation of a pending payment status.
 * - `pending`: Payment is not completed yet, and user didn't get his reward yet. But we will checking status for this payment.
 * - `captured`: Payment is completed successfully and user got reward for sure.
 * - `canceled`: Payment is canceled by user or payment provider. User didn't get reward. We won't check status for this payment anymore.
 * - `failed`: Payment is failed by some reason. User didn't get reward. We won't check status for this payment anymore.
 */
type PendingPaymentStatus = 'pending' | 'captured' | 'canceled' | 'failed';

/**
 * Some providers have a pending payment that needs to be checked later.
 * This type representing a pending payment type for such providers.
 *
 * For now it used only by Komoju provider. Previously it was also used by Xsolla provider too, but we removed Xsolla recently.
 */
export type PendingPayment = {
  /**
   * The pending payment unique ID.
   */
  id: string;

  /**
   * The payment provider used for this payment.
   * It will be always Komoju, we removed Xsolla recently.
   */
  paymentProvider: PendingPaymentProvider;
  /**
   * The payment method used for this payment.
   * This is not prefixed with 'komoju_'.
   * It will only komoju payment methods.
   */
  paymentMethod: KomojuPaymentMethod;

  /**
   * The amount paid for this payment.
   */
  price: number;

  /**
   * The currency used for this payment.
   */
  currency: string;

  /**
   * The credit card brand used for this payment.
   * Only for credit card payment methods.
   */
  creditCardBrand?: PurchaseProviderCreditCardBrand;

  /**
   * The Komoju order ID
   */
  purchaseId: string;
  /**
   * Purchased product ID
   */
  productId: ProductId;
  /**
   * When this payment was created.
   * We will use this to clean up old pending payments.
   */
  createdAt: number;
  /**
   * When payment status was changed
   */
  updatedAt: number;
  /**
   * The status of pending payment.
   * - `pending`: Payment is not completed yet, and user didn't get his reward yet. But we will checking status for this payment.
   * - `captured`: Payment is completed successfully and user got reward for sure.
   * - `canceled`: Payment is canceled by user or payment provider. User didn't get reward. We won't check status for this payment anymore.
   * - `failed`: Payment is failed by some reason. User didn't get reward. We won't check status for this payment anymore.
   */
  status: PendingPaymentStatus;
  /**
   * Feature analytics for this payment.
   */
  analytics: {
    feature: string;
    subFeature: string;
  };

  /**
   * WebPay analytics for this payment.
   */
  webPayAnalytics?: RedirectStateAnalytics;

  /**
   * Timestamp of the payment method selection.
   */
  webPayMethodTimestamp?: number;
};

/**
 * Payment API requirements, we can't use AsyncGettersAPI, because of ref circular dependencies.
 */
type PaymentAPI = {
  getOwnState: () => Promise<State>;
  date: { now: () => number };
  payments: {
    getKomojuPayment: (purchaseId: string) => Promise<{
      status: KomojuPaymentStatus;
    } | null>;
    cancelKomojuPayment: (purchaseId: string) => Promise<{
      status: KomojuPaymentStatus;
    } | null>;
  };
  reportError: (error: Error) => void;
};

/**
 * Get all pending payments.
 */
export function getAllPendingPayments(
  state: State,
): Immutable<PendingPayment[]> {
  const payments = state.pendingPayments as PendingPayment[];

  // We need to handle case where migrated items do not have price after migration
  const getPrice = (payment: PendingPayment) => {
    return payment.price === 0 &&
      payment.currency === 'JPY' &&
      payment.productId
      ? (webIAPPrices[payment.productId] ?? 0)
      : payment.price;
  };

  // Fix issue with payment methods migration
  const normalizePaymentMethod = (
    payment: PendingPayment,
  ): KomojuPaymentMethod => {
    const map: Record<string, KomojuPaymentMethod> = {
      credit_card: 'komoju_credit_card',
      rakutenpay: 'komoju_rakutenpay',
      paypay: 'komoju_paypay',
      paidy: 'komoju_paidy',
      aupay: 'komoju_aupay',
      docomo: 'komoju_docomo',
      softbank: 'komoju_softbank',
      au: 'komoju_au',
    };

    if (map[payment.paymentMethod]) {
      return map[payment.paymentMethod];
    }

    return payment.paymentMethod;
  };

  return payments.map((payment) => ({
    ...payment,
    // Make sure that we have prefixed payment method in state
    paymentMethod: normalizePaymentMethod(payment),
    // We need handle case where migrated items do not have price after migration
    price: getPrice(payment),
  })) as Immutable<PendingPayment[]>;
}

/**
 * Records payments that for some reason were notn added to the pending payments.
 * This may happen if there's an API error and we fail to record the payment in state.
 *
 * @param state - The state object containing pending payments.
 * @param purchases - The list of completed purchases to validate against pending payments.
 * @param now - The current timestamp used for tracking creation and updates.
 */
export function recordMissedPayments(
  state: MutableState,
  purchases: Immutable<PurchaseInfo[]>,
  now: number,
) {
  const pendingPurchaseIds = new Set(
    getAllPendingPayments(state).map((p) => p.purchaseId),
  );

  // Disconsider anything before 2025-02-19 JST. Payments that didn't require a redirect were not added to the pending payments list - so they would be considered unrecorded.
  // TODO: Remove this after 3 weeks of deployment.
  const cutoffDate = new Date('2025-02-19T00:00:00+09:00').getTime();

  const unrecorded = purchases
    .filter(
      (p) =>
        supportedPendingPaymentProviders.includes(
          p.algorithm as PendingPaymentProvider,
        ) && // Only consider supported methods.
        p.provider?.method && // Only consider purchases with a payment method.
        !pendingPurchaseIds.has(p.payment_id) && // Payment exists, but there is no corresponding pending payment in state.
        now - p.purchase_time < PENDING_PAYMENT_CLEANUP && // Only consider purchases that would appear in pending payments.
        p.purchase_time > cutoffDate, // Disconsider anything before 2025-02-19 JST.
    )
    .map((p) =>
      addPendingPayment(
        state,
        {
          paymentProvider: p.algorithm as PendingPaymentProvider, // Safe to cast, we already checked it in the filter above.
          paymentMethod: p.provider!.method as KomojuPaymentMethod, // Safe to access, we already checked it in the filter above.
          creditCardBrand: p.provider!.creditCardBrand,
          price: webIAPPrices[p.product_id as ProductId],
          currency: LOCAL_CURRENCY,
          purchaseId: p.payment_id,
          productId: p.product_id as ProductId,
          analytics: {
            feature: Feature.IAP._,
            subFeature: Feature.IAP.FULFILL_UNRECORDED_PAYMENTS,
          },
        },
        now,
      ),
    );

  return unrecorded;
}

/**
 * Adds a pending payment to the state.
 *
 * @param state - The mutable state object that holds the pending payments.
 * @param payment - The payment details excluding 'id', 'createdAt', 'updatedAt', and 'status'.
 * @param now - The current timestamp to be used for 'createdAt' and 'updatedAt' fields.
 * @returns The newly added pending payment object.
 */
export function addPendingPayment(
  state: MutableState,
  payment: Omit<PendingPayment, 'id' | 'createdAt' | 'updatedAt' | 'status'>,
  now: number,
): PendingPayment {
  const pendingPayment: PendingPayment = {
    // Should be unique for every platform, just platformId not enough to be unique.
    id: `${payment.paymentProvider}_${payment.purchaseId}`,
    ...payment,
    createdAt: now,
    updatedAt: now,
    status: 'pending',
  };

  // Save to state
  state.pendingPayments.push(pendingPayment);

  return pendingPayment;
}

/**
 * Determines whether a pending payment should be canceled based on its status and the current time.
 *
 * @param payment - The pending payment to evaluate.
 * @param now - The current time in milliseconds.
 * @returns `true` if the payment should be canceled, `false` otherwise.
 */
export function shouldCancelPendingPayment(
  payment: PendingPayment,
  now: number,
): boolean {
  if (payment.status === 'pending') {
    return payment.updatedAt + PENDING_PAYMENT_TIMEOUT < now;
  }

  return false;
}

/**
 * Updates the status of a pending payment in the state.
 *
 * @param state - The mutable state containing the pending payments.
 * @param opts - Options for updating the payment status.
 * @param opts.id - The ID of the payment to update.
 * @param opts.status - The new status to set for the payment. Must be a status other than 'pending'.
 * @param now - The current timestamp to set as the updated time for the payment.
 * @returns The updated pending payment object.
 *
 * @throws {Error} If the payment with the given ID is not found.
 * @throws {Error} If attempting to update a payment that is not in the 'pending' status.
 */
export function updatePendingPaymentStatus(
  state: MutableState,
  opts: { id: string; status: Exclude<PendingPaymentStatus, 'pending'> },
  now: number,
): PendingPayment {
  const payment = state.pendingPayments.find(
    (p) => p.id === opts.id,
  ) as PendingPayment;

  // If payment not found, throw an error.
  if (!payment) {
    throw new Error(`Pending payment not found: ${opts.id}`);
  }

  // Update only pending payments
  // Updating resolved, canceled, or failed payments is not allowed.
  switch (payment.status) {
    case 'pending':
      payment.status = opts.status;
      payment.updatedAt = now;
      break;
    case 'captured':
      throw new Error(`Resolved payment status cannot be changed: ${opts.id}`);
    case 'canceled':
      throw new Error(`Canceled payment status cannot be changed: ${opts.id}`);
    case 'failed':
      throw new Error(`Failed payment status cannot be changed: ${opts.id}`);
    default:
      assertNever(payment.status);
  }

  return { ...payment };
}

/**
 * Cleans up old resolved pending payments from the state.
 *
 * This function filters out pending payments that have been resolved
 * (i.e., have a status of 'captured', 'canceled', or 'failed') and
 * have been updated within a certain time frame defined by
 * `PENDING_PAYMENT_CLEANUP`.
 *
 * @param state - The mutable state object containing the pending payments.
 * @param now - The current timestamp used to determine the age of the payments.
 */
export function cleanupOldResolvedPendingPayments(
  state: MutableState,
  now: number,
) {
  state.pendingPayments = state.pendingPayments.filter(
    (p) =>
      p.status === 'pending' || now - p.updatedAt < PENDING_PAYMENT_CLEANUP,
  );
}

/**
 * Retrieves the status of pending payments.
 *
 * @param opts - Options for retrieving pending payments.
 * @param opts.limit - The maximum number of pending payments to retrieve.
 * @param api - The payment API instance to use for retrieving payment statuses.
 * @returns A promise that resolves to an array of pending payments with their actual statuses.
 */
export async function getPendingPaymentsStatusAsync(
  opts: { limit: number },
  api: PaymentAPI,
): Promise<PendingPayment[]> {
  const state = await api.getOwnState();

  // Get all pending payments that are not older than PENDING_PAYMENT_TIMEOUT
  const pendingPayments = getAllPendingPayments(state)
    .filter((p) => p.status === 'pending') // Only pending payments
    .sort((a, b) => b.createdAt - a.createdAt) // Sort by newest first
    .slice(0, opts.limit); // Limit pending payments, to avoid overloading the server

  // Get the actual statuses of the pending payments
  const actualStatusesPromises = pendingPayments.map((p) =>
    getPendingPaymentStatus(p, api),
  );

  // Wait for all result statuses in parallel
  const actualStatuses = await Promise.all(actualStatusesPromises);

  // Map the pending payments with their actual statuses
  return pendingPayments.map((payment, i) => {
    return {
      ...payment,
      status: actualStatuses[i],
    };
  });
}

/**
 * Retrieves the status of a pending payment asynchronously.
 *
 * @param opts - An object containing the pending payment ID.
 * @param opts.pendingPaymentId - The ID of the pending payment to check.
 * @param api - An instance of the PaymentAPI to interact with the payment system.
 * @returns A promise that resolves to the status of the pending payment, or undefined if the payment is not found.
 */
export async function getPendingPaymentStatusAsync(
  opts: { pendingPaymentId: string },
  api: PaymentAPI,
): Promise<PendingPaymentStatus | undefined> {
  const state = await api.getOwnState();

  // Get all pending payments that are not older than PENDING_PAYMENT_TIMEOUT
  const pendingPayment = getAllPendingPayments(state).find(
    (p) => p.id === opts.pendingPaymentId && p.status === 'pending',
  );

  // If payment not found, return undefined
  if (!pendingPayment) {
    return undefined;
  }

  return getPendingPaymentStatus(pendingPayment, api);
}

/**
 * Retrieves the status of a pending payment based on the platform type.
 *
 * @param payment - The pending payment object containing platform type and ID.
 * @param api - The payment API interface to interact with different payment platforms.
 * @returns A promise that resolves to the status of the pending payment.
 *
 * @remarks
 * - For 'komoju' platform type, it fetches the payment status from the Komoju API.
 * - If an error occurs while fetching the status, it reports the error and defaults to 'pending' status.
 * - For unsupported platform types, it throws an error.
 *
 * @throws Will throw an error if the platform type is not supported.
 */
export async function getPendingPaymentStatus(
  payment: PendingPayment,
  api: PaymentAPI,
): Promise<PendingPaymentStatus> {
  // Get the actual statuses of the pending payments
  switch (payment.paymentProvider) {
    case 'komoju': {
      // prettier-ignore
      return api.payments
        .getKomojuPayment(payment.purchaseId)
        .then((result) => normalizePendingStatus({ paymentProvider: 'komoju', status: result?.status ?? 'pending' }))
        .catch((error) => {
          api.reportError(createReportableError('error', error));
          return 'pending'; // Report and fallback to pending status
        });
    }
    default:
      assertNever(payment.paymentProvider);
  }
}

/**
 * Cancels a pending payment by its ID using the provided PaymentAPI.
 *
 * @param pendingPaymentId - The ID of the pending payment to be canceled.
 * @param api - The PaymentAPI instance used to interact with the payment provider.
 * @returns A promise that resolves to the status of the pending payment after attempting cancellation.
 * @throws Will throw an error if the pending payment is not found.
 */
export async function cancelPendingPaymentAsync(
  opts: { pendingPaymentId: string },
  api: PaymentAPI,
): Promise<PendingPaymentStatus> {
  const { pendingPaymentId } = opts;
  const state = await api.getOwnState();

  // Find the pending payment to be canceled
  const payment = getAllPendingPayments(state).find(
    (p) => p.id === pendingPaymentId,
  );

  // If payment not found, throw an error
  if (!payment) {
    throw new Error(`Pending payment not found: ${pendingPaymentId}`);
  }

  // Try to cancel the payment on the provider side
  switch (payment.paymentProvider) {
    case 'komoju': {
      return api.payments
        .cancelKomojuPayment(payment.purchaseId)
        .then((result) =>
          normalizePendingStatus({
            paymentProvider: 'komoju',
            status: result?.status ?? 'pending',
          }),
        )
        .catch((error) => {
          api.reportError(createReportableError('error', error));
          return 'pending'; // Report and fallback to pending status
        });
    }
    default:
      assertNever(payment.paymentProvider);
  }
}

/**
 * Convert the platform payment status to internal pending payment status.
 *
 * This function converts the Komoju payment status to a generic pending payment status.
 *
 * @param opts - The options for normalizing the pending status.
 * @param opts.platformType - The platform type, which should be 'komoju'.
 * @param opts.status - The payment status from Komoju.
 *
 * @returns The normalized pending payment status.
 *
 * @throws Will throw an error if the platform type or status is not recognized.
 */
function normalizePendingStatus(opts: {
  paymentProvider: PendingPaymentProvider;
  status: KomojuPaymentStatus;
}): PendingPaymentStatus {
  /**
   * Convert Komoju payment status to a generic pending payment status
   * Possible payment statuses in KOMOJU:
   *
   * - **Pending**: The customer has initiated the payment but hasn't completed the necessary actions.
   *   This status often appears when the customer lands on the checkout screen but hasn't entered their information or has abandoned the process.
   *
   * - **Authorized**: The payment information has been successfully received, but funds are not yet transferred.
   *   For credit card transactions, this means the card payment is authorized, and you need to capture the payment to receive funds.
   *
   * - **Captured**: The payment is complete, and KOMOJU has received the funds.
   *   At this point, you can proceed to deliver the goods or services to the customer.
   *
   * - **Expired**: The customer didn't complete the payment within the allowed timeframe, causing the payment to expire.
   *   You can view the expiration details in the dashboard.
   *
   * - **Cancelled**: The payment was actively cancelled by either the merchant or the customer, preventing any further processing.
   *
   * - **Refunded**: The payment has been fully refunded to the customer.
   *   Note that partially refunded payments remain in the "captured" status.
   *
   * - **Failed**: The payment couldn't be executed, often due to issues like incorrect credit card information.
   *   Somehow we don't have this status for getKomojuPayment API
   */
  if (opts.paymentProvider === 'komoju') {
    switch (opts.status) {
      case 'authorized': // See comment above
      case 'pending':
        return 'pending';
      case 'captured':
        return 'captured';
      case 'cancelled':
      case 'expired':
      case 'refunded': // Lets treat refunded as canceled, but it maybe not correct
        return 'canceled';
      case 'failed':
        return 'failed';
      default:
        assertNever(opts.status);
    }
  }

  assertNever(opts.paymentProvider);
}
